import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { authenticatedFetch } from '../lib/apiClient';

const isTransientNetworkError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }
  const message = error.message.toLowerCase();
  return message.includes('failed to fetch') || message.includes('networkerror');
};

/**
 * 通知類型
 */
export type NotificationType = 'info' | 'success' | 'warning' | 'error' | 'system';

/**
 * 通知優先級
 */
export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

/**
 * 通知介面
 */
export interface Notification {
  id: string;
  user_id: string;
  user_role?: string;
  title: string;
  message: string;
  type: NotificationType;
  related_type?: string;
  related_id?: string;
  action_url?: string;
  is_read: boolean;
  read_at?: string;
  priority: NotificationPriority;
  created_at: string;
  updated_at: string;
}

/**
 * NotificationContext 類型
 */
interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  
  // 操作方法
  fetchNotifications: (unreadOnly?: boolean) => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

/**
 * NotificationProvider 組件
 */
export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 獲取通知列表
   */
  const fetchNotifications = useCallback(async (unreadOnly = false) => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`/api/notifications?unread_only=${unreadOnly}`);
      
      if (!response.ok) {
        throw new Error('獲取通知失敗');
      }

      const data = await response.json();
      if (data.success) {
        setNotifications(data.data || []);
      } else {
        throw new Error(data.error || '獲取通知失敗');
      }
    } catch (err) {
      console.error('獲取通知失敗:', err);
      setError(err instanceof Error ? err.message : '獲取通知失敗');
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  /**
   * 獲取未讀數量
   */
  const refreshUnreadCount = useCallback(async () => {
    if (!user) return;

    try {
      const response = await authenticatedFetch('/api/notifications/unread-count');
      
      if (!response.ok) {
        throw new Error('獲取未讀數量失敗');
      }

      const data = await response.json();
      if (data.success) {
        setUnreadCount(data.data.count || 0);
      }
    } catch (err) {
      if (!isTransientNetworkError(err)) {
        console.error('獲取未讀數量失敗:', err);
      }
    }
  }, [user]);

  /**
   * 標記為已讀
   */
  const markAsRead = useCallback(async (notificationId: string) => {
    if (!user) return;

    try {
      const response = await authenticatedFetch(`/api/notifications/${notificationId}/read`, {
        method: 'PUT'
      });
      
      if (!response.ok) {
        throw new Error('標記已讀失敗');
      }

      const data = await response.json();
      if (data.success) {
        // 更新本地狀態
        setNotifications(prev => 
          prev.map(n => 
            n.id === notificationId 
              ? { ...n, is_read: true, read_at: new Date().toISOString() }
              : n
          )
        );
        // 更新未讀數量
        await refreshUnreadCount();
      }
    } catch (err) {
      console.error('標記已讀失敗:', err);
      throw err;
    }
  }, [user, refreshUnreadCount]);

  /**
   * 標記全部為已讀
   */
  const markAllAsRead = useCallback(async () => {
    if (!user) return;

    try {
      const response = await authenticatedFetch('/api/notifications/read-all', {
        method: 'PUT'
      });
      
      if (!response.ok) {
        throw new Error('標記全部已讀失敗');
      }

      const data = await response.json();
      if (data.success) {
        // 更新本地狀態
        setNotifications(prev => 
          prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
        );
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('標記全部已讀失敗:', err);
      throw err;
    }
  }, [user]);

  /**
   * 刪除通知
   */
  const deleteNotification = useCallback(async (notificationId: string) => {
    if (!user) return;

    try {
      const response = await authenticatedFetch(`/api/notifications/${notificationId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error('刪除通知失敗');
      }

      // 更新本地狀態
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      // 更新未讀數量
      await refreshUnreadCount();
    } catch (err) {
      console.error('刪除通知失敗:', err);
      throw err;
    }
  }, [user, refreshUnreadCount]);

  // 初始化：載入通知和未讀數量
  useEffect(() => {
    if (user) {
      fetchNotifications();
      refreshUnreadCount();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user, fetchNotifications, refreshUnreadCount]);

  // 定期刷新未讀數量（每30秒）
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      refreshUnreadCount();
    }, 30000); // 30秒

    return () => clearInterval(interval);
  }, [user, refreshUnreadCount]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    isLoading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshUnreadCount
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

/**
 * useNotifications Hook
 */
export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
